// src/types/supabase.ts
var Constants = {
  public: {
    Enums: {}
  }
};

// src/types/builds.ts
var BuildArch = /* @__PURE__ */ ((BuildArch2) => {
  BuildArch2["arm64"] = "arm64";
  BuildArch2["x64"] = "x64";
  return BuildArch2;
})(BuildArch || {});
var BuildPlatform = /* @__PURE__ */ ((BuildPlatform2) => {
  BuildPlatform2["windows"] = "win32";
  BuildPlatform2["macOS"] = "darwin";
  return BuildPlatform2;
})(BuildPlatform || {});
var StaticBuildChannel = /* @__PURE__ */ ((StaticBuildChannel2) => {
  StaticBuildChannel2["alpha"] = "alpha";
  StaticBuildChannel2["beta"] = "beta";
  StaticBuildChannel2["latest"] = "latest";
  StaticBuildChannel2["rc"] = "rc";
  StaticBuildChannel2["trustedTesters"] = "trusted-testers";
  return StaticBuildChannel2;
})(StaticBuildChannel || {});
var RELEASE_PROMOTION_DESTINATIONS = ["latest" /* latest */, "beta" /* beta */];
var RELEASE_PROMOTION_TARGETS = [
  { platform: "darwin" /* macOS */, arch: "arm64" /* arm64 */ },
  { platform: "darwin" /* macOS */, arch: "x64" /* x64 */ },
  { platform: "win32" /* windows */, arch: "arm64" /* arm64 */ },
  { platform: "win32" /* windows */, arch: "x64" /* x64 */ }
];
function isStaticChannel(channel) {
  return Object.values(StaticBuildChannel).includes(channel);
}
var BuildDistribution = /* @__PURE__ */ ((BuildDistribution2) => {
  BuildDistribution2["legacy"] = "legacy";
  BuildDistribution2["highlight"] = "highlight";
  return BuildDistribution2;
})(BuildDistribution || {});
var PRIMARY_DISTRIBUTION = "highlight" /* highlight */;
function isValidDistribution(value) {
  return typeof value === "string" && Object.values(BuildDistribution).includes(value);
}
function normalizeDistribution(distribution) {
  return isValidDistribution(distribution) ? distribution : "legacy" /* legacy */;
}
function isLegacyDistribution(distribution) {
  return normalizeDistribution(distribution) === "legacy" /* legacy */;
}
var CDN_PREFIX_BY_DISTRIBUTION = {
  ["legacy" /* legacy */]: "",
  ["highlight" /* highlight */]: "highlight"
};
function distributionCdnPrefix(distribution) {
  return CDN_PREFIX_BY_DISTRIBUTION[normalizeDistribution(distribution)];
}
function joinCdnPath(...segments) {
  return segments.map((segment) => typeof segment === "string" ? segment.replace(/^\/+|\/+$/g, "") : "").filter((segment) => segment.length > 0).join("/");
}
var CDN_ORIGIN = "https://cdn.highlightai.com";
function distributionCdnUrl(distribution, ...segments) {
  return `${CDN_ORIGIN}/${joinCdnPath(distributionCdnPrefix(distribution), ...segments)}`;
}
function buildCdnObjectPath(location, filename) {
  const { distribution, dynamic, channelValue, platform, arch } = location;
  return joinCdnPath(
    distributionCdnPrefix(distribution),
    dynamic ? `builds/${channelValue}` : "releases",
    platform,
    arch,
    filename
  );
}
function buildCdnObjectUrl(location, filename) {
  return `${CDN_ORIGIN}/${buildCdnObjectPath(location, filename)}`;
}
function manifestCdnObjectPath(location, suffix = ".yml") {
  const macSuffix = location.platform === "darwin" /* macOS */ ? "-mac" : "";
  return buildCdnObjectPath(location, `${location.channelValue}${macSuffix}${suffix}`);
}
function buildTargetKey(platform, arch, distribution) {
  return `${platform}-${arch}-${normalizeDistribution(distribution)}`;
}
function distributionKeySegment(distribution) {
  return isLegacyDistribution(distribution) ? [] : [normalizeDistribution(distribution)];
}

// ../billing-shared/src/index.ts
var BILLING_PLAN = {
  FREE: "free",
  LEGACY_PRO: "pro",
  LEGACY_PRO_TRIAL: "pro-trial",
  EAT: "eat",
  LITE: "lite",
  PRO2: "pro2",
  MAX: "max",
  LITE_TRIAL: "lite-trial",
  PRO2_TRIAL: "pro2-trial"
};
var HIGHLIGHT_BILLING_PLANS = [
  BILLING_PLAN.FREE,
  BILLING_PLAN.LEGACY_PRO,
  BILLING_PLAN.LEGACY_PRO_TRIAL,
  BILLING_PLAN.LITE,
  BILLING_PLAN.PRO2,
  BILLING_PLAN.MAX,
  BILLING_PLAN.LITE_TRIAL,
  BILLING_PLAN.PRO2_TRIAL
];
var V2_PLAN_TIERS = [BILLING_PLAN.LITE, BILLING_PLAN.PRO2, BILLING_PLAN.MAX];
var V2_TRIAL_PLAN_BY_TIER = {
  [BILLING_PLAN.LITE]: BILLING_PLAN.LITE_TRIAL,
  [BILLING_PLAN.PRO2]: BILLING_PLAN.PRO2_TRIAL
};
var V2_TRIAL_PLANS = [
  V2_TRIAL_PLAN_BY_TIER[BILLING_PLAN.LITE],
  V2_TRIAL_PLAN_BY_TIER[BILLING_PLAN.PRO2]
];
var V2_PLANS = [...V2_PLAN_TIERS, ...V2_TRIAL_PLANS];
var V2_TIER_BY_TRIAL_PLAN = Object.fromEntries(
  Object.entries(V2_TRIAL_PLAN_BY_TIER).map(([tier, trial]) => [trial, tier])
);
var isV2Plan = (plan) => plan != null && V2_PLANS.includes(plan);
var isV2TrialPlan = (plan) => plan != null && V2_TRIAL_PLANS.includes(plan);
var isV2PlanTier = (plan) => plan != null && V2_PLAN_TIERS.includes(plan);
var v2TierFor = (plan) => {
  if (!isV2Plan(plan)) return null;
  return isV2TrialPlan(plan) ? V2_TIER_BY_TRIAL_PLAN[plan] : plan;
};
var isTrialPlan = (plan) => plan === BILLING_PLAN.LEGACY_PRO_TRIAL || isV2TrialPlan(plan);
var planEndsAt = (profile) => profile.trialEndsAt ?? profile.cancelsAt ?? profile.currentPeriod.endingAt;
var V2_TRIAL_DAYS = 14;
var V2_TRIAL_ENDING_LEAD_DAYS = 3;
var DISCOUNT_GRANT_TERMS = {
  "early-tester-50": { percentOff: 50, durationMonths: 12, label: "Early tester discount" }
};
function discountedCents(listCents, percentOff) {
  return Math.round(listCents * (100 - percentOff) / 100);
}
function discountedUsd(listUsd, percentOff) {
  return discountedCents(Math.round(listUsd * 100), percentOff) / 100;
}
var LEGACY_PRO_PRICING = {
  label: "Pro",
  displayName: "Highlight Pro",
  monthlyPriceUsd: 20,
  checkoutDescription: "Premium AI Models, High quality meeting transcription, and more."
};
var V2_PLAN_PRICING = {
  [BILLING_PLAN.LITE]: {
    label: "Lite",
    displayName: "Highlight Lite",
    monthlyPriceUsd: 35,
    monthlyCredits: 500,
    checkoutDescription: "Meeting transcription, reminders, and chat."
  },
  [BILLING_PLAN.PRO2]: {
    label: "Pro",
    displayName: "Highlight Pro",
    monthlyPriceUsd: 60,
    monthlyCredits: 3e3,
    checkoutDescription: "Meeting transcription, reminders, skills, and chat."
  },
  [BILLING_PLAN.MAX]: {
    label: "Max",
    displayName: "Highlight Max",
    monthlyPriceUsd: 200,
    monthlyCredits: 8e3,
    checkoutDescription: "Meeting transcription, reminders, chat, custom skills, and white glove support."
  }
};
var planDisplayName = (plan) => {
  if (plan === BILLING_PLAN.FREE) return "Free";
  const tier = v2TierFor(plan);
  const base = tier ? V2_PLAN_PRICING[tier].displayName : LEGACY_PRO_PRICING.displayName;
  return isTrialPlan(plan) ? `${base} Trial` : base;
};
var paidPlanTierName = (plan) => {
  if (plan === BILLING_PLAN.LEGACY_PRO) return LEGACY_PRO_PRICING.label;
  return isV2PlanTier(plan) ? V2_PLAN_PRICING[plan].label : null;
};
var V2_TRIAL_MONTHLY_CREDITS_BY_PLAN = {
  [BILLING_PLAN.LITE_TRIAL]: V2_PLAN_PRICING[BILLING_PLAN.LITE].monthlyCredits,
  [BILLING_PLAN.PRO2_TRIAL]: V2_PLAN_PRICING[BILLING_PLAN.PRO2].monthlyCredits
};
var HL1_TRIAL_DAYS = 30;
var hl1TrialDays = (perkTier, tier) => perkTier === BILLING_PLAN.PRO2 || tier === BILLING_PLAN.LITE ? HL1_TRIAL_DAYS : V2_TRIAL_DAYS;
var hl1TrialDaysByTier = (perkTier) => ({
  [BILLING_PLAN.LITE]: hl1TrialDays(perkTier, BILLING_PLAN.LITE),
  [BILLING_PLAN.PRO2]: hl1TrialDays(perkTier, BILLING_PLAN.PRO2)
});
var HL1_PAID_SOURCES = [
  "orb-pro",
  "orb-pro2",
  "stripe-pro",
  "stripe-pro-monthly",
  "stripe-pro-yearly",
  "stripe-unlimited"
];
var HL1_PREVIOUS_MONTHLY_PRICE_USD = {
  "orb-pro": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "orb-pro2": V2_PLAN_PRICING[BILLING_PLAN.PRO2].monthlyPriceUsd,
  "stripe-pro": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "stripe-pro-monthly": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "stripe-pro-yearly": null,
  "stripe-unlimited": null
};
var MEETING_HOURS_BY_PLAN = {
  [BILLING_PLAN.FREE]: 0,
  [BILLING_PLAN.LEGACY_PRO]: 30,
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: 30,
  [BILLING_PLAN.EAT]: null,
  [BILLING_PLAN.LITE]: 20,
  [BILLING_PLAN.PRO2]: 50,
  [BILLING_PLAN.MAX]: 80,
  [BILLING_PLAN.LITE_TRIAL]: 20,
  [BILLING_PLAN.PRO2_TRIAL]: 50
};
var AUDIO_MINUTES_METRIC_NAME = "Audio Note Minutes";
var getMeetingHoursForPlan = (plan, edition) => {
  const table = MEETING_HOURS_BY_EDITION[edition];
  return plan != null && plan in table ? table[plan] : table[BILLING_PLAN.FREE];
};
var MINUTE_TOP_UP_MINUTES_PER_USD = 60;
var MINUTE_TOP_UP_USD_PER_MINUTE = 1 / MINUTE_TOP_UP_MINUTES_PER_USD;
var MINUTE_TOP_UP_MIN_MINUTES = 60;
var MINUTE_TOP_UP_MAX_MINUTES = 6e3;
var SKILL_TIERS = ["free", "lite", "pro", "max"];
var PRICING_EDITIONS = ["v2", "v2-revamp"];
var pricingEditionFor = (freeTierPlanRevamp) => freeTierPlanRevamp ? "v2-revamp" : "v2";
var ALL_PLAN_FEATURES = {
  planner: true,
  skillRuns: true,
  skillTier: "max",
  createCustomSkills: true,
  screenshotIngest: true,
  monthlyMeetingBriefs: null,
  monthlyActionUnlocks: null,
  meetingSummaries: true,
  modelChoice: true
};
var V2_FREE_PLAN_FEATURES = {
  ...ALL_PLAN_FEATURES,
  meetingSummaries: false,
  modelChoice: false
};
var LITE_PLAN_FEATURES = {
  planner: true,
  skillRuns: true,
  skillTier: "lite",
  createCustomSkills: false,
  screenshotIngest: false,
  monthlyMeetingBriefs: null,
  monthlyActionUnlocks: null,
  meetingSummaries: true,
  modelChoice: true
};
var FREE_PLAN_FEATURES = {
  ...LITE_PLAN_FEATURES,
  skillTier: "free",
  monthlyMeetingBriefs: 10,
  monthlyActionUnlocks: 3,
  modelChoice: false
};
var PRO_PLAN_FEATURES = {
  ...ALL_PLAN_FEATURES,
  skillTier: "pro",
  createCustomSkills: false
};
var V2_FEATURES_BY_PLAN = {
  [BILLING_PLAN.FREE]: V2_FREE_PLAN_FEATURES,
  [BILLING_PLAN.LEGACY_PRO]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.EAT]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LITE]: LITE_PLAN_FEATURES,
  [BILLING_PLAN.PRO2]: PRO_PLAN_FEATURES,
  [BILLING_PLAN.MAX]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LITE_TRIAL]: LITE_PLAN_FEATURES,
  [BILLING_PLAN.PRO2_TRIAL]: PRO_PLAN_FEATURES
};
var PLAN_FEATURES_BY_EDITION = {
  v2: V2_FEATURES_BY_PLAN,
  "v2-revamp": {
    ...V2_FEATURES_BY_PLAN,
    [BILLING_PLAN.FREE]: FREE_PLAN_FEATURES,
    [BILLING_PLAN.PRO2]: ALL_PLAN_FEATURES,
    [BILLING_PLAN.PRO2_TRIAL]: ALL_PLAN_FEATURES
  }
};
var isSkillTierAllowed = (features, tier) => SKILL_TIERS.indexOf(tier) <= SKILL_TIERS.indexOf(features.skillTier);
var getPlanFeaturesForPlan = (plan, edition) => {
  const table = PLAN_FEATURES_BY_EDITION[edition];
  if (plan == null || !(plan in table)) return ALL_PLAN_FEATURES;
  return table[plan];
};
var cheapestPlanWith = (edition, has) => PLAN_LINEUP_BY_EDITION[edition].filter(isV2PlanTier).find((plan) => has(getPlanFeaturesForPlan(plan, edition))) ?? BILLING_PLAN.MAX;
var v2TermsFor = (tier) => ({
  label: V2_PLAN_PRICING[tier].label,
  monthlyPriceUsd: V2_PLAN_PRICING[tier].monthlyPriceUsd,
  monthlyCredits: V2_PLAN_PRICING[tier].monthlyCredits,
  monthlyMeetingHours: MEETING_HOURS_BY_PLAN[tier],
  monthlyMeetingBriefs: null
});
var V2_PLAN_TERMS = {
  [BILLING_PLAN.FREE]: {
    label: "Free",
    monthlyPriceUsd: 0,
    monthlyCredits: 0,
    monthlyMeetingHours: 0,
    monthlyMeetingBriefs: null
  },
  [BILLING_PLAN.LITE]: v2TermsFor(BILLING_PLAN.LITE),
  [BILLING_PLAN.PRO2]: v2TermsFor(BILLING_PLAN.PRO2),
  [BILLING_PLAN.MAX]: v2TermsFor(BILLING_PLAN.MAX)
};
var V2_REVAMP_PLAN_TERMS = {
  [BILLING_PLAN.FREE]: {
    label: "Free",
    monthlyPriceUsd: 0,
    monthlyCredits: 500,
    monthlyMeetingHours: 10,
    monthlyMeetingBriefs: FREE_PLAN_FEATURES.monthlyMeetingBriefs
  },
  [BILLING_PLAN.LITE]: V2_PLAN_TERMS[BILLING_PLAN.LITE],
  [BILLING_PLAN.PRO2]: {
    ...V2_PLAN_TERMS[BILLING_PLAN.PRO2],
    monthlyPriceUsd: 35,
    monthlyCredits: 8e3,
    monthlyMeetingHours: 200
  },
  [BILLING_PLAN.MAX]: { ...V2_PLAN_TERMS[BILLING_PLAN.MAX], monthlyCredits: 6e4, monthlyMeetingHours: 200 }
};
var PLAN_TERMS_BY_EDITION = {
  v2: V2_PLAN_TERMS,
  "v2-revamp": V2_REVAMP_PLAN_TERMS
};
var PLAN_LINEUP_BY_EDITION = {
  v2: V2_PLAN_TIERS,
  "v2-revamp": [BILLING_PLAN.FREE, BILLING_PLAN.PRO2, BILLING_PLAN.MAX]
};
var getPlanTerms = (plan, edition) => PLAN_TERMS_BY_EDITION[edition][plan];
var OFFERED_PLANS_BY_EDITION = {
  v2: HIGHLIGHT_BILLING_PLANS,
  "v2-revamp": [
    BILLING_PLAN.FREE,
    BILLING_PLAN.LEGACY_PRO_TRIAL,
    BILLING_PLAN.PRO2,
    BILLING_PLAN.MAX,
    BILLING_PLAN.PRO2_TRIAL
  ]
};
var ORB_MANAGED_PLANS_BY_EDITION = {
  v2: V2_PLANS,
  "v2-revamp": [...V2_PLANS, BILLING_PLAN.FREE]
};
var getOrbPlanTerms = (plan, edition) => {
  if (isV2TrialPlan(plan)) {
    const { monthlyCredits: monthlyCredits2, monthlyMeetingHours: monthlyMeetingHours2 } = getPlanTerms(V2_TIER_BY_TRIAL_PLAN[plan], edition);
    return { monthlyPriceUsd: null, monthlyCredits: monthlyCredits2, monthlyMeetingHours: monthlyMeetingHours2 };
  }
  const { monthlyPriceUsd, monthlyCredits, monthlyMeetingHours } = getPlanTerms(plan, edition);
  return { monthlyPriceUsd, monthlyCredits, monthlyMeetingHours };
};
var isMeteredPlan = (plan, edition) => plan != null && ORB_MANAGED_PLANS_BY_EDITION[edition].includes(plan);
var meetingHoursOn = (edition) => ({
  ...MEETING_HOURS_BY_PLAN,
  ...Object.fromEntries(
    ORB_MANAGED_PLANS_BY_EDITION[edition].map((plan) => [plan, getOrbPlanTerms(plan, edition).monthlyMeetingHours])
  )
});
var MEETING_HOURS_BY_EDITION = {
  v2: meetingHoursOn("v2"),
  "v2-revamp": meetingHoursOn("v2-revamp")
};
var INTERNAL_BILLING_PLANS = [...HIGHLIGHT_BILLING_PLANS, BILLING_PLAN.EAT];
var EXTERNAL_PLAN_ID_BY_PLAN = {
  [BILLING_PLAN.FREE]: "highlight-free",
  [BILLING_PLAN.LEGACY_PRO]: "highlight-pro",
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: "highlight-pro-trial",
  [BILLING_PLAN.EAT]: "highlight-eat",
  [BILLING_PLAN.LITE]: "highlight-lite",
  [BILLING_PLAN.PRO2]: "highlight-pro2",
  [BILLING_PLAN.MAX]: "highlight-max",
  [BILLING_PLAN.LITE_TRIAL]: "highlight-lite-trial",
  [BILLING_PLAN.PRO2_TRIAL]: "highlight-pro2-trial"
};
var EXTERNAL_BILLING_PLAN_IDS = [
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.FREE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO_TRIAL],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.EAT],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.MAX],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE_TRIAL],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2_TRIAL]
];
var PAID_RECURRING_EXTERNAL_PLAN_IDS = [
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.MAX]
];
var PLAN_BY_EXTERNAL_PLAN_ID = Object.fromEntries(
  Object.entries(EXTERNAL_PLAN_ID_BY_PLAN).map(([plan, externalId]) => [externalId, plan])
);
var CREDITS_PER_USD = 100;
var CREDIT_TOP_UP_MIN_USD = 5;
var CREDIT_TOP_UP_MAX_USD = 500;

// src/types/subscription.ts
var BILLING_SIMULATION_MODES = ["off", "out-of-hours", "out-of-credits", "plan-ended"];
var BILLING_SIMULATION_STORE_KEY = "dev.billingSimulation";
var defaultPaywallState = {
  plan: void 0,
  isTrial: false,
  credits: { used: 0, total: 0, remaining: 0, percentage: 0 },
  meetingHours: { used: 0, total: 0, remaining: 0, percentage: 0 },
  atWarning: false,
  atLimit: false,
  nagBarsDismissed: {},
  nagBarsPermanentlyDismissed: false,
  missedMeetings: 0,
  missedHighlights: 0,
  isLoading: true,
  isLegacyBilling: false
};
var defaultSubscriptionInfo = {
  active: false,
  isLegacyBilling: false
};
var defaultUnifiedBillingState = {
  paywall: defaultPaywallState,
  subscription: defaultSubscriptionInfo,
  billingProfile: null,
  usage: null,
  lastUpdated: 0,
  creditConsumption: null
};
export {
  AUDIO_MINUTES_METRIC_NAME,
  BILLING_PLAN,
  BILLING_SIMULATION_MODES,
  BILLING_SIMULATION_STORE_KEY,
  BuildArch,
  BuildDistribution,
  BuildPlatform,
  CDN_ORIGIN,
  CREDITS_PER_USD,
  CREDIT_TOP_UP_MAX_USD,
  CREDIT_TOP_UP_MIN_USD,
  Constants,
  DISCOUNT_GRANT_TERMS,
  HL1_PAID_SOURCES,
  HL1_TRIAL_DAYS,
  INTERNAL_BILLING_PLANS,
  LEGACY_PRO_PRICING,
  MEETING_HOURS_BY_PLAN,
  MINUTE_TOP_UP_MAX_MINUTES,
  MINUTE_TOP_UP_MINUTES_PER_USD,
  MINUTE_TOP_UP_MIN_MINUTES,
  PLAN_LINEUP_BY_EDITION,
  PRICING_EDITIONS,
  PRIMARY_DISTRIBUTION,
  RELEASE_PROMOTION_DESTINATIONS,
  RELEASE_PROMOTION_TARGETS,
  SKILL_TIERS,
  StaticBuildChannel,
  V2_PLANS,
  V2_PLAN_PRICING,
  V2_TIER_BY_TRIAL_PLAN,
  V2_TRIAL_DAYS,
  V2_TRIAL_ENDING_LEAD_DAYS,
  V2_TRIAL_PLAN_BY_TIER,
  buildCdnObjectPath,
  buildCdnObjectUrl,
  buildTargetKey,
  cheapestPlanWith,
  defaultPaywallState,
  defaultSubscriptionInfo,
  defaultUnifiedBillingState,
  discountedUsd,
  distributionCdnPrefix,
  distributionCdnUrl,
  distributionKeySegment,
  getMeetingHoursForPlan,
  getPlanFeaturesForPlan,
  getPlanTerms,
  hl1TrialDays,
  hl1TrialDaysByTier,
  isLegacyDistribution,
  isMeteredPlan,
  isSkillTierAllowed,
  isStaticChannel,
  isTrialPlan,
  isV2Plan,
  isV2PlanTier,
  isV2TrialPlan,
  isValidDistribution,
  joinCdnPath,
  manifestCdnObjectPath,
  normalizeDistribution,
  paidPlanTierName,
  planDisplayName,
  planEndsAt,
  pricingEditionFor,
  v2TierFor
};
//# sourceMappingURL=index.js.map